import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Timer, Users, MapPin, Gavel, X, Heart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AppIcon from "@/components/AppIcon";

/**
 * AuctionDetail — full auction screen wired to /api/live-world/auctions/:id.
 * Countdown updates live from server_time + closes_at. BID button opens a
 * confirmation sheet. Bidding calls the backend; SSE in LiveWorld picks up
 * the fanout so the grid updates without a manual refresh.
 */
function useCountdown(iso) {
  const [ms, setMs] = useState(() => (iso ? new Date(iso) - new Date() : 0));
  useEffect(() => {
    if (!iso) return undefined;
    const t = setInterval(() => setMs(new Date(iso) - new Date()), 1000);
    return () => clearInterval(t);
  }, [iso]);
  if (ms == null) return { text: "—", closed: false };
  if (ms <= 0) return { text: "CLOSED", closed: true, ms: 0 };
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return { text: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`, closed: false, ms };
}

export default function AuctionDetail() {
  const nav = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [showBid, setShowBid] = useState(false);
  const [amount, setAmount] = useState("");
  const [placing, setPlacing] = useState(false);
  const [watching, setWatching] = useState(false);
  const [toggling, setToggling] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/live-world/auctions/${id}`);
      setData(r.data);
      const min = (r.data?.auction?.current_bid || 0) + (r.data?.auction?.bid_increment || 100);
      setAmount(String(min));
    } catch {
      setData(null);
    } finally {
      setBusy(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Load watch status (best-effort — anonymous user just sees "Watch")
  useEffect(() => {
    let ignore = false;
    api.get(`/live-world/auctions/${id}/watch`)
      .then((r) => { if (!ignore) setWatching(!!r.data?.watching); })
      .catch(() => { /* anon; leave watching=false */ });
    return () => { ignore = true; };
  }, [id]);

  const toggleWatch = async () => {
    setToggling(true);
    const prev = watching;
    setWatching(!prev);   // optimistic
    try {
      if (prev) {
        await api.delete(`/live-world/auctions/${id}/watch`);
        toast.success("Removed from watchlist");
      } else {
        await api.post(`/live-world/auctions/${id}/watch`);
        toast.success("Added to watchlist");
      }
    } catch (e) {
      setWatching(prev);
      toast.error(e?.response?.status === 401
        ? "Please log in to watch this auction."
        : "Couldn't update watchlist.");
    } finally {
      setToggling(false);
    }
  };

  const auction = data?.auction || null;
  const countdown = useCountdown(auction?.closes_at);
  const currency = auction?.currency || "INR";

  const placeBid = async () => {
    const amt = Number(amount);
    if (!amt || Number.isNaN(amt)) { toast.error("Enter a valid amount"); return; }
    setPlacing(true);
    try {
      const r = await api.post(`/live-world/auctions/${id}/bid`, { amount: amt });
      toast.success(`Bid placed at ${currency} ${amt.toLocaleString()}`);
      setShowBid(false);
      // Optimistically bump the current bid + count
      setData((d) => d ? {
        ...d,
        auction: { ...d.auction, current_bid: r.data.current_bid, bid_count: r.data.bid_count },
      } : d);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bid failed");
    } finally {
      setPlacing(false);
    }
  };

  if (busy && !auction) {
    return <div className="min-h-[70vh] grid place-items-center text-stone-500"><Loader2 className="animate-spin"/></div>;
  }
  if (!auction) {
    return (
      <div className="min-h-[70vh] grid place-items-center text-stone-500 text-sm">
        Auction not found. <button className="ml-2 underline" onClick={() => nav("/live-world")}>Back</button>
      </div>
    );
  }

  const minNextBid = (auction.current_bid || 0) + (auction.bid_increment || 100);
  const bidStatus = countdown.closed ? "closed" : "live";

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 pb-32" data-testid="lw-auction-detail">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-2xl mx-auto px-3 py-2 flex items-center gap-2">
          <button onClick={() => nav(-1)} className="p-2 -ml-1 rounded-full hover:bg-stone-100" data-testid="lw-auction-back">
            <ArrowLeft size={18}/>
          </button>
          <div className="font-black text-stone-900 truncate">{auction.title}</div>
          <button
            onClick={toggleWatch}
            disabled={toggling}
            title={watching ? "Unwatch" : "Watch auction"}
            data-testid="lw-auction-watch"
            className={`ml-auto p-2 rounded-full transition-colors ${
              watching ? "text-rose-500 hover:bg-rose-50" : "text-stone-500 hover:bg-stone-100"
            }`}
          >
            <Heart size={18} className={watching ? "fill-rose-500" : ""}/>
          </button>
          <div className="text-[10px] uppercase font-black px-2 py-1 rounded-full text-white"
               style={{ background: bidStatus === "closed" ? "#71717a" : "#2563eb" }}>
            {bidStatus === "closed" ? "🔒 CLOSED" : "🔵 LIVE"}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-3 space-y-3">
        {/* Hero */}
        <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-stone-100 grid place-items-center">
          {auction.images?.[0] || auction.image ? (
            <img src={auction.images?.[0] || auction.image} alt="" className="w-full h-full object-cover"/>
          ) : (
            <AppIcon name={auction.category} size={140} fallback={<div className="text-6xl">🔨</div>}/>
          )}
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
            <div className="bg-black/70 text-white px-3 py-1.5 rounded-full text-xs font-black inline-flex items-center gap-1.5">
              <Timer size={12}/> {countdown.text}
            </div>
            <div className="bg-white/95 text-stone-900 px-3 py-1.5 rounded-full text-xs font-black inline-flex items-center gap-1.5">
              <Users size={12}/> {auction.bid_count || 0} bids
            </div>
          </div>
        </div>

        {/* Price panel */}
        <div className="bg-white rounded-2xl border border-stone-200 p-4 space-y-2" data-testid="lw-auction-price">
          <div className="flex items-baseline gap-2">
            <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">Current bid</div>
          </div>
          <div className="text-3xl font-black text-stone-900">
            {currency === "INR" ? "₹" : currency + " "}{(auction.current_bid || 0).toLocaleString("en-IN")}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-600 font-bold">
            <span>Starting bid: {currency === "INR" ? "₹" : currency + " "}{(auction.starting_bid || 0).toLocaleString("en-IN")}</span>
            <span>Increment: {currency === "INR" ? "₹" : currency + " "}{(auction.bid_increment || 100).toLocaleString("en-IN")}</span>
            {auction.city && <span className="inline-flex items-center gap-1"><MapPin size={11}/> {auction.city}</span>}
          </div>
        </div>

        {/* Description */}
        {auction.description && (
          <div className="bg-white rounded-2xl border border-stone-200 p-4 text-sm text-stone-700 leading-relaxed">
            {auction.description}
          </div>
        )}

        {/* Recent bids */}
        <div className="bg-white rounded-2xl border border-stone-200 p-4">
          <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-2">Recent bids</div>
          {(data?.bids || []).length === 0 && (
            <div className="text-xs text-stone-400 py-3 text-center">No bids yet. Be the first to bid.</div>
          )}
          <div className="space-y-1.5" data-testid="lw-auction-bids">
            {(data?.bids || []).slice(0, 10).map((b) => (
              <div key={b.bid_id} className="flex items-center justify-between text-sm">
                <div className="text-stone-600 font-mono">{b.bidder_masked}</div>
                <div className="font-black text-stone-900">
                  {b.currency === "INR" ? "₹" : b.currency + " "}{Number(b.amount).toLocaleString("en-IN")}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-[10px] text-stone-400 text-center italic">
          Bidding is binding within this marketplace's terms. AINA does not
          automatically bid on your behalf; every bid requires your tap.
        </div>
      </div>

      {/* Sticky BID */}
      <div className="fixed left-0 right-0 bottom-16 bg-white border-t border-stone-200 p-3 z-40">
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <div className="flex-1 text-[11px] text-stone-500 font-bold leading-tight">
            Min next bid<br/>
            <span className="text-stone-900 text-sm font-black">
              {currency === "INR" ? "₹" : currency + " "}{minNextBid.toLocaleString("en-IN")}
            </span>
          </div>
          <button
            onClick={() => setShowBid(true)}
            disabled={countdown.closed}
            className="inline-flex items-center gap-1.5 px-6 py-3 rounded-full bg-gradient-to-br from-[#E05E36] to-[#c94e2a] text-white font-black text-sm shadow-lg disabled:opacity-40 disabled:from-stone-400 disabled:to-stone-500"
            data-testid="lw-auction-bid-open"
          >
            <Gavel size={16}/> BID
          </button>
        </div>
      </div>

      {/* Bid confirmation sheet */}
      {showBid && (
        <div className="fixed inset-0 z-[70] bg-black/60" onClick={() => setShowBid(false)}>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-5 space-y-3 max-h-[80vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()} data-testid="lw-auction-bid-sheet">
            <div className="flex items-center justify-between">
              <div className="font-black text-stone-900 text-lg">Confirm your bid</div>
              <button onClick={() => setShowBid(false)} className="p-1 rounded-full hover:bg-stone-100"><X size={18}/></button>
            </div>
            <div className="text-sm text-stone-600">{auction.title}</div>
            <div className="bg-stone-50 rounded-2xl p-3 flex items-center gap-2">
              <div className="text-lg font-black text-stone-500">{currency === "INR" ? "₹" : currency}</div>
              <input
                type="number"
                inputMode="numeric"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="flex-1 bg-transparent outline-none text-2xl font-black text-stone-900"
                data-testid="lw-auction-bid-amount"
                min={minNextBid}
                step={auction.bid_increment || 100}
              />
            </div>
            <div className="text-[11px] text-stone-500 font-bold">
              Minimum: {currency === "INR" ? "₹" : currency + " "}{minNextBid.toLocaleString("en-IN")} ·
              Countdown: <span className="text-stone-900">{countdown.text}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[0, 1, 3].map((mult) => {
                const v = minNextBid + (auction.bid_increment || 100) * mult;
                return (
                  <button key={mult}
                    onClick={() => setAmount(String(v))}
                    className="py-2 rounded-full bg-stone-100 text-stone-800 text-xs font-black hover:bg-stone-200">
                    {currency === "INR" ? "₹" : currency + " "}{v.toLocaleString("en-IN")}
                  </button>
                );
              })}
            </div>
            <button
              onClick={placeBid}
              disabled={placing || countdown.closed}
              className="w-full py-3 rounded-full bg-gradient-to-br from-[#E05E36] to-[#c94e2a] text-white font-black shadow-lg disabled:opacity-40"
              data-testid="lw-auction-bid-confirm"
            >
              {placing ? "Placing…" : `Confirm bid — ${currency === "INR" ? "₹" : currency + " "}${Number(amount || 0).toLocaleString("en-IN")}`}
            </button>
            <div className="text-[10px] text-stone-400 text-center">
              This bid is binding. You cannot retract a live bid.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
